/**
 * Keyboard state for the desktop build.
 *
 * Kate specified arrow keys to move ("there's a left arrow, like on a computer there's
 * like you hit the arrows to move"). WASD is accepted alongside them because it costs
 * nothing and doesn't replace anything she asked for.
 *
 * Attack is not hard-coded: she changed her mind from "R" to "you can choose a random
 * button like in settings", so the bound code is read from Settings at query time.
 */
export interface MoveIntent {
  forward: number
  /** -1 left, +1 right. Real strafing now that looking is its own axis. */
  strafe: number
  jump: boolean
  crouch: boolean
  /** Kate priced sprinting in hunger, so it exists as its own state. */
  sprint: boolean
}

export class Keyboard {
  private readonly held = new Set<string>()
  private readonly onDown = (e: KeyboardEvent): void => {
    this.held.add(e.code)
  }
  private readonly onUp = (e: KeyboardEvent): void => {
    this.held.delete(e.code)
  }
  /** A tab-switch mid-keypress otherwise leaves the key stuck down forever. */
  private readonly onBlur = (): void => {
    this.held.clear()
  }

  attach(): void {
    window.addEventListener('keydown', this.onDown)
    window.addEventListener('keyup', this.onUp)
    window.addEventListener('blur', this.onBlur)
  }

  detach(): void {
    window.removeEventListener('keydown', this.onDown)
    window.removeEventListener('keyup', this.onUp)
    window.removeEventListener('blur', this.onBlur)
    this.held.clear()
  }

  /** Drop every held key — used when gameplay input is suspended. */
  releaseAll(): void {
    this.held.clear()
  }

  /** Force a key's state. For the dev harness only — real input goes through events. */
  setDown(code: string, down: boolean): void {
    if (down) this.held.add(code)
    else this.held.delete(code)
  }

  isDown(code: string): boolean {
    return this.held.has(code)
  }

  readIntent(): MoveIntent {
    const forward =
      (this.isDown('ArrowUp') || this.isDown('KeyW') ? 1 : 0) -
      (this.isDown('ArrowDown') || this.isDown('KeyS') ? 1 : 0)
    const strafe =
      (this.isDown('ArrowRight') || this.isDown('KeyD') ? 1 : 0) -
      (this.isDown('ArrowLeft') || this.isDown('KeyA') ? 1 : 0)
    return {
      forward,
      strafe,
      jump: this.isDown('Space'),
      crouch: this.isDown('ShiftLeft') || this.isDown('ShiftRight'),
      sprint: this.isDown('ControlLeft') || this.isDown('ControlRight'),
    }
  }
}
